<template>
  <div class="score-query-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">
          <el-icon class="title-icon"><Search /></el-icon>
          成绩查询发布
        </h1>
        <p class="page-subtitle">按“上传表格、设置查询、发布分享”三步创建查分项目</p>
      </div>
      <el-button type="primary" class="create-btn" @click="openWizard()">
        <el-icon><Plus /></el-icon>新建查分项目
      </el-button>
    </div>

    <div v-if="schemes.length === 0 && !loading" class="empty-state">
      <el-icon :size="64"><DataAnalysis /></el-icon>
      <p class="empty-title">还没有查分项目</p>
      <p class="empty-desc">上传电子表格后即可选择列名生成查询页</p>
      <el-button type="primary" @click="openWizard()">立即创建</el-button>
    </div>

    <div v-else class="schemes-grid" v-loading="loading">
      <div v-for="scheme in schemes" :key="scheme.id" class="scheme-card">
        <div class="card-header">
          <div class="card-title-row">
            <span class="scheme-title">{{ scheme.title }}</span>
            <el-tag :type="getSchemeStatusType(scheme)" class="status-tag">{{ getSchemeStatusText(scheme) }}</el-tag>
          </div>
          <p class="scheme-desc">{{ scheme.query_prompt || scheme.description || '暂无提示文字' }}</p>
        </div>
        <div class="card-meta">
          <span><el-icon><Key /></el-icon>查询条件：{{ scheme.query_conditions.join('、') || '未设置' }}</span>
          <span><el-icon><Document /></el-icon>数据字段：{{ scheme.headers.length ? scheme.headers.length + ' 列' : '未导入' }}</span>
          <span><el-icon><DataAnalysis /></el-icon>成绩分析：{{ scheme.analysis_enabled ? formatMetrics(scheme.analysis_metrics) : '未开启' }}</span>
        </div>
        <div class="card-actions">
          <el-button text class="action-btn copy-btn" @click="copyLink(scheme)">
            <el-icon><Link /></el-icon>复制链接
          </el-button>
          <el-button text class="action-btn share-btn" @click="openShareDialog(scheme)">
            <el-icon><View /></el-icon>链接/二维码
          </el-button>
          <el-button text class="action-btn edit-btn" @click="openWizard(scheme)">
            <el-icon><Edit /></el-icon>编辑三步配置
          </el-button>
          <el-button text class="action-btn preview-btn" :disabled="!scheme.headers.length" @click="openPreviewDialog(scheme)">
            <el-icon><View /></el-icon>数据预览
          </el-button>
          <el-button text class="action-btn logs-btn" @click="openLogDialog(scheme)">
            <el-icon><DataAnalysis /></el-icon>查询记录
          </el-button>
          <el-button text class="action-btn toggle-btn" @click="toggleActive(scheme)">
            <el-icon><component :is="scheme.is_active ? 'VideoPause' : 'VideoPlay'" /></el-icon>{{ scheme.is_active ? '暂停' : '开启' }}
          </el-button>
          <el-button text class="action-btn delete-btn" @click="confirmDelete(scheme)">
            <el-icon><Delete /></el-icon>
          </el-button>
        </div>
        <div class="card-link-bar">{{ getPublicUrl(scheme.code) }}</div>
      </div>
    </div>

    <el-dialog
      v-model="wizardVisible"
      title="创建成绩查分"
      width="1180px"
      :close-on-click-modal="false"
      class="wizard-dialog"
      @closed="resetWizard"
    >
      <el-steps :active="wizardStepActive" finish-status="success" align-center class="wizard-steps">
        <el-step title="上传电子表格" />
        <el-step title="设置查询页面" />
        <el-step title="发布分享" />
      </el-steps>

      <section v-if="wizardStep === 0" class="wizard-panel">
        <div class="wizard-guide">
          <div class="guide-copy">
            <span>创建引导</span>
            <strong>从一张 Excel 开始发布查分入口</strong>
            <p>系统会读取第一行作为字段列名，上传后可预览前 10 条数据，再选择姓名、学号等字段作为查询条件。</p>
          </div>
          <div class="guide-steps">
            <div><b>1</b>上传表格</div>
            <div><b>2</b>选择查询条件</div>
            <div><b>3</b>扫码或链接发布</div>
          </div>
        </div>
        <div class="upload-layout">
          <div class="upload-card">
            <el-upload
              class="upload-area"
              drag
              :auto-upload="false"
              :on-change="handleWizardFileChange"
              :on-remove="handleWizardFileRemove"
              :limit="1"
              :on-exceed="() => ElMessage.warning('每次只能上传一个文件')"
              accept=".xls,.xlsx,.xlsm"
              ref="wizardUploadRef"
            >
              <el-icon class="upload-area-icon"><Upload /></el-icon>
              <div class="el-upload__text">将 Excel 文件拖到此处，或<em> 点击选择文件</em></div>
              <template #tip>
                <div class="el-upload__tip">支持 .xls / .xlsx / .xlsm，第一行必须是表头。</div>
              </template>
            </el-upload>
            <div v-if="selectedFile" class="selected-file">已选择：{{ selectedFile.name }}</div>
            <div v-if="wizardUploadError" class="upload-error">
              <el-icon><Warning /></el-icon>{{ wizardUploadError }}
            </div>
            <div class="step-actions">
              <el-button type="primary" @click="uploadWizardFile" :loading="uploading" :disabled="!selectedFile">
                上传并识别表头
              </el-button>
            </div>
          </div>

          <div class="preview-box">
            <div class="box-title">上传预览</div>
            <p v-if="!wizardScheme" class="muted-text">上传成功后，这里会显示前 10 条数据。</p>
            <template v-else>
              <div class="preview-meta">已识别 {{ wizardForm.headers.length }} 个字段，共 {{ previewTotal }} 条记录</div>
              <el-table :data="previewRecords" border stripe size="small" height="300" class="preview-table desktop-preview-table">
                <el-table-column
                  v-for="header in wizardForm.headers"
                  :key="header"
                  :prop="`data.${header}`"
                  :label="header"
                  min-width="96"
                >
                  <template #default="{ row }">{{ row.data[header] }}</template>
                </el-table-column>
              </el-table>
              <div class="mobile-record-list">
                <div v-for="(row, idx) in previewRecords" :key="row.id || idx" class="mobile-record-card">
                  <div class="mobile-record-title">第 {{ idx + 1 }} 条</div>
                  <div v-for="header in wizardForm.headers" :key="header" class="mobile-record-row">
                    <span>{{ header }}</span>
                    <strong>{{ row.data[header] || '-' }}</strong>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </section>

      <section v-else-if="wizardStep === 1" class="wizard-panel settings-grid">
        <el-form :model="wizardForm" label-width="96px" class="settings-form">
          <el-form-item label="查询标题" required>
            <el-input v-model="wizardForm.title" placeholder="例如：2026年高一期末成绩查询" maxlength="60" show-word-limit />
          </el-form-item>
          <el-form-item label="查询条件" required>
            <el-select
              v-model="wizardForm.query_conditions"
              multiple
              filterable
              collapse-tags
              collapse-tags-tooltip
              placeholder="请选择用于身份验证的列名"
              style="width: 100%"
            >
              <el-option v-for="header in wizardForm.headers" :key="header" :label="header" :value="header" />
            </el-select>
            <p class="field-hint">建议选择 2 个字段，如“姓名 + 学号/身份证号/准考证号”。</p>
          </el-form-item>
          <el-form-item label="主题模板">
            <el-radio-group v-model="wizardForm.theme" class="theme-options">
              <el-radio-button label="blue">简洁蓝</el-radio-button>
              <el-radio-button label="green">校园绿</el-radio-button>
              <el-radio-button label="gold">喜报金</el-radio-button>
              <el-radio-button label="dark">深色正式</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="提示文字">
            <el-input
              v-model="wizardForm.query_prompt"
              type="textarea"
              :rows="3"
              placeholder="例如：请输入学生姓名和准考证号查询本次考试成绩。"
              maxlength="500"
              show-word-limit
            />
          </el-form-item>
          <el-form-item label="成绩分析">
            <div class="analysis-row">
              <el-switch v-model="wizardForm.analysis_enabled" active-text="显示" inactive-text="不显示" />
              <el-checkbox-group v-model="wizardForm.analysis_metrics" :disabled="!wizardForm.analysis_enabled">
                <el-checkbox label="max">最高分</el-checkbox>
                <el-checkbox label="min">最低分</el-checkbox>
                <el-checkbox label="avg">均分</el-checkbox>
              </el-checkbox-group>
            </div>
          </el-form-item>
          <el-form-item label="查询图片">
            <div class="image-config">
              <el-input v-model="wizardForm.query_image_url" placeholder="可粘贴图片 URL，也可以直接上传" clearable />
              <el-upload
                :auto-upload="false"
                :show-file-list="false"
                accept=".jpg,.jpeg,.png,.webp,.gif"
                :on-change="handleImageFileChange"
              >
                <el-button :loading="imageUploading">上传图片</el-button>
              </el-upload>
            </div>
            <img v-if="wizardForm.query_image_url" :src="resolveAssetUrl(wizardForm.query_image_url)" class="image-preview" alt="查询页图片预览" />
          </el-form-item>
          <el-form-item label="结果字段">
            <el-select
              v-model="wizardForm.display_fields"
              multiple
              filterable
              collapse-tags
              collapse-tags-tooltip
              placeholder="请选择结果页展示字段"
              style="width: 100%"
              @change="syncFieldGroups"
            >
              <el-option v-for="header in wizardForm.headers" :key="header" :label="header" :value="header" />
            </el-select>
            <p class="field-hint">不想展示身份证号、手机号等敏感字段时，可在这里取消勾选。</p>
          </el-form-item>
          <el-form-item label="结果分组">
            <div class="group-editor">
              <div class="group-toolbar">
                <el-button size="small" @click="autoGroupFields">自动分组</el-button>
                <el-button size="small" @click="addFieldGroup">新增分组</el-button>
              </div>
              <div v-for="(group, groupIndex) in wizardForm.field_groups" :key="groupIndex" class="group-edit-card">
                <div class="group-title-row">
                  <el-input
                    v-model="group.title"
                    size="small"
                    maxlength="20"
                    placeholder="分组标题"
                    @blur="normalizeFieldGroups"
                  />
                  <el-button text type="danger" size="small" @click="removeFieldGroup(groupIndex)">删除</el-button>
                </div>
                <el-select
                  v-model="group.fields"
                  multiple
                  filterable
                  collapse-tags
                  collapse-tags-tooltip
                  size="small"
                  placeholder="选择该分组展示字段"
                  style="width: 100%"
                  @change="normalizeFieldGroups"
                >
                  <el-option
                    v-for="header in wizardForm.display_fields"
                    :key="header"
                    :label="header"
                    :value="header"
                  />
                </el-select>
              </div>
              <div v-if="!wizardForm.field_groups.length" class="group-empty">
                暂无分组，保存时会按展示字段自动生成。
              </div>
            </div>
          </el-form-item>
          <el-form-item label="发布状态">
            <el-switch v-model="wizardForm.is_active" active-text="开启查询" inactive-text="暂不发布" />
          </el-form-item>
        </el-form>

        <div class="live-preview">
          <div class="box-title">右侧预览</div>
          <div class="phone-shell">
            <div class="mock-page" :class="`mock-theme-${wizardForm.theme}`">
              <img v-if="wizardForm.query_image_url" :src="resolveAssetUrl(wizardForm.query_image_url)" class="mock-image" alt="" />
              <h2>{{ wizardForm.title || '成绩查询标题' }}</h2>
              <p>{{ wizardForm.query_prompt || '请填写以下信息查询个人成绩。' }}</p>
              <div v-for="cond in previewConditions" :key="cond" class="mock-input">{{ cond }}</div>
              <button>查询成绩</button>
              <div class="mock-result-title">结果页预览</div>
              <div v-for="header in previewResultHeaders" :key="header" class="mock-row">
                <span>{{ header }}</span><strong>{{ sampleRecord[header] || '示例数据' }}</strong>
              </div>
              <div v-if="wizardForm.analysis_enabled" class="mock-analysis">
                {{ formatMetrics(wizardForm.analysis_metrics) || '成绩分析' }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-else class="wizard-panel finish-panel">
        <div class="publish-share-card">
          <el-result icon="success" title="查分项目已保存" sub-title="家长或学生可扫码查看，也可以通过链接打开查询页" />
          <div class="share-content" v-if="wizardScheme">
            <div class="qr-card">
              <img v-if="wizardQrSrc" :src="wizardQrSrc" alt="查分二维码" />
              <div v-else class="qr-error">{{ wizardQrError || '二维码生成失败' }}</div>
            </div>
            <div class="share-info">
              <div class="share-label">查分链接</div>
              <div class="share-url">{{ wizardShareUrl }}</div>
              <div class="share-actions">
                <el-button type="primary" @click="copyLink(wizardScheme)">复制链接</el-button>
                <el-button @click="openPublicLink(wizardScheme)">链接查看</el-button>
                <el-button @click="downloadQrCode(wizardScheme)">下载二维码</el-button>
                <el-button @click="downloadPoster(wizardScheme)">下载查分海报</el-button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <template #footer>
        <el-button @click="wizardVisible = false">关闭</el-button>
        <el-button v-if="wizardStep > 0" @click="wizardStep--">上一步</el-button>
        <el-button v-if="wizardStep === 0" type="primary" :disabled="!wizardScheme" @click="wizardStep = 1">下一步</el-button>
        <el-button v-if="wizardStep === 1" type="primary" :loading="submitting" @click="saveWizardSettings">保存并进入发布</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="shareDialogVisible"
      :title="shareScheme ? `${shareScheme.title} - 分享查看` : '分享查看'"
      width="460px"
      class="share-dialog"
    >
      <div v-if="shareScheme" class="share-dialog-content">
        <div class="qr-card dialog-qr-card">
          <img v-if="shareQrSrc" :src="shareQrSrc" alt="查分二维码" />
          <div v-else class="qr-error">{{ shareQrError || '二维码生成失败' }}</div>
        </div>
        <div class="share-label">查分链接</div>
        <div class="share-url">{{ getPublicUrl(shareScheme.code) }}</div>
      </div>
      <template #footer>
        <el-button @click="shareDialogVisible = false">关闭</el-button>
        <el-button @click="openPublicLink(shareScheme)">链接查看</el-button>
        <el-button @click="downloadQrCode(shareScheme)">下载二维码</el-button>
        <el-button @click="downloadPoster(shareScheme)">下载海报</el-button>
        <el-button type="primary" @click="copyLink(shareScheme)">复制链接</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="previewDialogVisible"
      :title="`数据预览 - ${previewScheme?.title}`"
      width="90%"
      class="preview-dialog"
    >
      <div class="preview-meta preview-meta-bar" v-if="previewScheme">
        <span>共 {{ previewTotal }} 条记录</span>
        <span>字段 {{ previewScheme.headers.length }} 列</span>
      </div>
      <el-table :data="previewRecords" border stripe v-loading="previewLoading" size="small" height="520" class="preview-table desktop-preview-table">
        <el-table-column v-for="header in (previewScheme?.headers || [])" :key="header" :label="header" min-width="100">
          <template #default="{ row }">{{ row.data[header] }}</template>
        </el-table-column>
      </el-table>
      <div v-loading="previewLoading" class="mobile-record-list dialog-mobile-list">
        <div v-for="(row, idx) in previewRecords" :key="row.id || idx" class="mobile-record-card">
          <div class="mobile-record-title">第 {{ (previewPage - 1) * previewLimit + idx + 1 }} 条</div>
          <div v-for="header in (previewScheme?.headers || [])" :key="header" class="mobile-record-row">
            <span>{{ header }}</span>
            <strong>{{ row.data[header] || '-' }}</strong>
          </div>
        </div>
      </div>
      <div class="preview-pagination">
        <el-pagination
          v-model:current-page="previewPage"
          :page-size="previewLimit"
          :total="previewTotal"
          layout="prev, pager, next, total"
          @current-change="loadPreviewPage"
        />
      </div>
    </el-dialog>

    <el-dialog
      v-model="logDialogVisible"
      :title="`查询记录 - ${logScheme?.title || ''}`"
      width="92%"
      class="log-dialog"
    >
      <div class="preview-meta preview-meta-bar" v-if="logScheme">
        <span>共 {{ logTotal }} 条查询记录</span>
        <span>按查询时间倒序展示</span>
      </div>
      <el-table :data="logRecords" border stripe v-loading="logLoading" size="small" height="520" class="log-table desktop-preview-table">
        <el-table-column label="查询时间" min-width="150">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="查询人/条件" min-width="190">
          <template #default="{ row }">
            <div class="log-kv-list">
              <span v-for="(value, key) in row.query_data" :key="key">{{ key }}：{{ value || '-' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 'success' ? 'success' : 'warning'" size="small">
              {{ row.status === 'success' ? '成功' : '未匹配' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="匹配数据" min-width="260">
          <template #default="{ row }">
            <div v-if="row.matched_records?.length" class="matched-summary">
              <div v-for="(record, index) in row.matched_records" :key="index">
                {{ summarizeMatchedRecord(record) }}
              </div>
            </div>
            <span v-else class="muted-text">无匹配数据</span>
          </template>
        </el-table-column>
        <el-table-column prop="ip_address" label="IP" min-width="130" />
        <el-table-column label="设备" min-width="220">
          <template #default="{ row }">{{ simplifyUserAgent(row.user_agent) }}</template>
        </el-table-column>
      </el-table>
      <div v-loading="logLoading" class="mobile-record-list dialog-mobile-list">
        <div v-for="row in logRecords" :key="row.id" class="mobile-record-card log-mobile-card">
          <div class="mobile-record-title">
            <span>{{ formatDateTime(row.created_at) }}</span>
            <el-tag :type="row.status === 'success' ? 'success' : 'warning'" size="small">
              {{ row.status === 'success' ? '成功' : '未匹配' }}
            </el-tag>
          </div>
          <div class="mobile-record-row">
            <span>查询条件</span>
            <strong>{{ summarizeQueryData(row.query_data) }}</strong>
          </div>
          <div class="mobile-record-row">
            <span>匹配数据</span>
            <strong>{{ row.matched_records?.length ? summarizeMatchedRecord(row.matched_records[0]) : '无匹配数据' }}</strong>
          </div>
          <div class="mobile-record-row">
            <span>IP / 设备</span>
            <strong>{{ row.ip_address || '-' }} / {{ simplifyUserAgent(row.user_agent) }}</strong>
          </div>
        </div>
      </div>
      <div class="preview-pagination">
        <el-pagination
          v-model:current-page="logPage"
          :page-size="logLimit"
          :total="logTotal"
          layout="prev, pager, next, total"
          @current-change="loadLogPage"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Search, Plus, DataAnalysis, Key, Document, Link, Upload, View, Edit,
  Delete, VideoPause, VideoPlay, Warning
} from '@element-plus/icons-vue'
import { scoreQueryApi } from '@/api'

const schemes = ref([])
const loading = ref(false)
const wizardVisible = ref(false)
const wizardStep = ref(0)
const wizardScheme = ref(null)
const wizardUploadRef = ref(null)
const selectedFile = ref(null)
const wizardUploadError = ref('')
const uploading = ref(false)
const submitting = ref(false)
const imageUploading = ref(false)

const wizardForm = ref(emptyWizardForm())

const previewDialogVisible = ref(false)
const previewScheme = ref(null)
const previewRecords = ref([])
const previewPage = ref(1)
const previewLimit = 50
const previewTotal = ref(0)
const previewLoading = ref(false)
const logDialogVisible = ref(false)
const logScheme = ref(null)
const logRecords = ref([])
const logPage = ref(1)
const logLimit = 50
const logTotal = ref(0)
const logLoading = ref(false)
const shareDialogVisible = ref(false)
const shareScheme = ref(null)
const shareQrSrc = ref('')
const shareQrError = ref('')

const previewConditions = computed(() => wizardForm.value.query_conditions.length ? wizardForm.value.query_conditions : ['姓名', '准考证号'])
const sampleRecord = computed(() => previewRecords.value[0]?.data || {})
const previewResultHeaders = computed(() => wizardForm.value.headers.slice(0, 8))
const wizardStepActive = computed(() => wizardStep.value === 2 ? 3 : wizardStep.value)
const wizardShareUrl = computed(() => wizardScheme.value ? getPublicUrl(wizardScheme.value.code) : '')
const wizardQrSrc = computed(() => buildQrSrc(wizardShareUrl.value))
const wizardQrError = ref('')

onMounted(loadSchemes)

function emptyWizardForm() {
  return {
    title: '',
    description: '',
    query_conditions: [],
    query_prompt: '',
    query_image_url: '',
    theme: 'blue',
    display_fields: [],
    field_groups: [],
    analysis_enabled: false,
    analysis_metrics: ['max', 'min', 'avg'],
    headers: [],
    is_active: true
  }
}

async function loadSchemes() {
  loading.value = true
  try {
    schemes.value = await scoreQueryApi.getSchemes()
  } catch (e) {
    ElMessage.error('加载查分项目失败')
  } finally {
    loading.value = false
  }
}

function getPublicUrl(code) {
  return `${window.location.origin}/query/${code}`
}

function getApiOrigin() {
  const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
  if (/^https?:\/\//i.test(baseUrl)) {
    return new URL(baseUrl).origin
  }
  return window.location.origin
}

function resolveAssetUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `${getApiOrigin()}${url}`
}

function formatMetrics(metrics = []) {
  const names = { max: '最高分', min: '最低分', avg: '均分' }
  return metrics.map(metric => names[metric]).filter(Boolean).join('、') || '未选择'
}

function isSchemeReady(scheme) {
  return scheme?.headers?.length > 0 && scheme?.query_conditions?.length > 0
}

function getSchemeStatusType(scheme) {
  if (!isSchemeReady(scheme)) return 'warning'
  return scheme.is_active ? 'success' : 'info'
}

function getSchemeStatusText(scheme) {
  if (!isSchemeReady(scheme)) return '待设置'
  return scheme.is_active ? '查询中' : '已暂停'
}

function copyLink(scheme) {
  if (!scheme) return
  const url = getPublicUrl(scheme.code)
  navigator.clipboard.writeText(url).then(() => {
    ElMessage.success('查分链接已复制到剪贴板')
  }).catch(() => {
    ElMessage.info(url)
  })
}

function buildQrSrc(url) {
  if (!url) return ''
  const code = url.split('/').pop()
  const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
  return `${baseUrl}/public/score-query/${code}/qr`
}

function openShareDialog(scheme) {
  shareScheme.value = scheme
  const url = getPublicUrl(scheme.code)
  shareQrSrc.value = buildQrSrc(url)
  shareQrError.value = ''
  shareDialogVisible.value = true
}

function openPublicLink(scheme) {
  if (!scheme) return
  window.open(getPublicUrl(scheme.code), '_blank', 'noopener,noreferrer')
}

function autoBuildGroups(headers, queryConditions, displayFields) {
  const fields = displayFields?.length ? displayFields : headers
  const basicKeywords = ['姓名', '学号', '班级', '年级', '考号', '准考证', '身份证']
  const summaryKeywords = ['总分', '排名', '名次', '均分', '平均', '等级']
  const basic = fields.filter(field => queryConditions.includes(field) || basicKeywords.some(k => field.includes(k)))
  const summary = fields.filter(field => !basic.includes(field) && summaryKeywords.some(k => field.includes(k)))
  const scores = fields.filter(field => !basic.includes(field) && !summary.includes(field))
  return [
    basic.length ? { title: '基本信息', fields: basic } : null,
    scores.length ? { title: '各科成绩', fields: scores } : null,
    summary.length ? { title: '汇总分析', fields: summary } : null
  ].filter(Boolean)
}

function autoGroupFields() {
  wizardForm.value.field_groups = autoBuildGroups(
    wizardForm.value.headers,
    wizardForm.value.query_conditions,
    wizardForm.value.display_fields
  )
}

function syncFieldGroups() {
  normalizeFieldGroups()
  wizardForm.value.field_groups = wizardForm.value.field_groups
    .map(group => ({
      ...group,
      fields: group.fields.filter(field => wizardForm.value.display_fields.includes(field))
    }))
    .filter(group => group.fields.length)
  if (!wizardForm.value.field_groups.length) autoGroupFields()
}

function normalizeFieldGroups() {
  const validFields = new Set(wizardForm.value.display_fields)
  wizardForm.value.field_groups = wizardForm.value.field_groups
    .map((group, index) => ({
      title: String(group.title || `分组 ${index + 1}`).trim(),
      fields: Array.from(new Set(group.fields || [])).filter(field => validFields.has(field))
    }))
    .filter(group => group.title || group.fields.length)
}

function addFieldGroup() {
  wizardForm.value.field_groups.push({
    title: `分组 ${wizardForm.value.field_groups.length + 1}`,
    fields: []
  })
}

function removeFieldGroup(index) {
  wizardForm.value.field_groups.splice(index, 1)
  if (!wizardForm.value.field_groups.length) autoGroupFields()
}

async function handleImageFileChange(file) {
  if (!wizardScheme.value) {
    ElMessage.warning('请先上传电子表格创建查分项目')
    return
  }
  imageUploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file.raw || file)
    const res = await scoreQueryApi.uploadImage(wizardScheme.value.id, formData)
    wizardForm.value.query_image_url = res.url
    ElMessage.success('图片上传成功')
  } catch (e) {
  } finally {
    imageUploading.value = false
  }
}

function sanitizeFilename(value) {
  return String(value || '查分二维码')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 60)
}

async function downloadQrCode(scheme) {
  if (!scheme) return
  const url = buildQrSrc(getPublicUrl(scheme.code))
  const filename = `${sanitizeFilename(scheme.title)}_二维码.png`
  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) throw new Error('二维码下载失败')
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
    ElMessage.success('二维码已开始下载')
  } catch (e) {
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    ElMessage.info('已打开二维码图片，可长按或右键保存')
  }
}

async function downloadPoster(scheme) {
  if (!scheme) return
  const canvas = document.createElement('canvas')
  canvas.width = 900
  canvas.height = 1280
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#f8fbff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const gradient = ctx.createLinearGradient(0, 0, 900, 420)
  gradient.addColorStop(0, '#0891b2')
  gradient.addColorStop(1, '#0f766e')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 900, 420)

  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 54px sans-serif'
  wrapCanvasText(ctx, scheme.title || '成绩查询', 80, 120, 740, 68)
  ctx.font = '30px sans-serif'
  wrapCanvasText(ctx, scheme.query_prompt || scheme.description || '扫码进入成绩查询页面', 80, 260, 740, 42)

  ctx.fillStyle = '#ffffff'
  roundRect(ctx, 170, 480, 560, 560, 28)
  ctx.fill()
  try {
    const qrImg = await loadImage(buildQrSrc(getPublicUrl(scheme.code)))
    ctx.drawImage(qrImg, 250, 560, 400, 400)
  } catch (e) {
    ctx.fillStyle = '#b91c1c'
    ctx.font = '24px sans-serif'
    ctx.fillText('二维码加载失败，请使用链接查看', 250, 760)
  }

  ctx.fillStyle = '#0f172a'
  ctx.font = 'bold 34px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('扫码查询成绩', 450, 1100)
  ctx.fillStyle = '#64748b'
  ctx.font = '24px sans-serif'
  wrapCanvasText(ctx, getPublicUrl(scheme.code), 160, 1150, 580, 32)
  ctx.textAlign = 'left'

  const link = document.createElement('a')
  link.href = canvas.toDataURL('image/png')
  link.download = `${sanitizeFilename(scheme.title)}_查分海报.png`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function wrapCanvasText(ctx, text, x, y, maxWidth, lineHeight) {
  const chars = String(text).split('')
  let line = ''
  chars.forEach((char) => {
    const testLine = line + char
    if (ctx.measureText(testLine).width > maxWidth && line) {
      ctx.fillText(line, x, y)
      line = char
      y += lineHeight
    } else {
      line = testLine
    }
  })
  if (line) ctx.fillText(line, x, y)
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

function openWizard(scheme = null) {
  if (!scheme || typeof scheme !== 'object' || scheme instanceof Event || !('id' in scheme)) {
    scheme = null
  }
  wizardVisible.value = true
  wizardStep.value = scheme?.headers?.length ? 1 : 0
  selectedFile.value = null
  wizardUploadError.value = ''
  wizardScheme.value = scheme
  wizardForm.value = scheme ? {
    title: scheme.title || '',
    description: scheme.description || '',
    query_conditions: [...(scheme.query_conditions || [])],
    query_prompt: scheme.query_prompt || scheme.description || '',
    query_image_url: scheme.query_image_url || '',
    theme: scheme.theme || 'blue',
    display_fields: scheme.display_fields?.length ? [...scheme.display_fields] : [...(scheme.headers || [])],
    field_groups: scheme.field_groups?.length ? JSON.parse(JSON.stringify(scheme.field_groups)) : autoBuildGroups(scheme.headers || [], scheme.query_conditions || [], scheme.headers || []),
    analysis_enabled: !!scheme.analysis_enabled,
    analysis_metrics: scheme.analysis_metrics?.length ? [...scheme.analysis_metrics] : ['max', 'min', 'avg'],
    headers: [...(scheme.headers || [])],
    is_active: scheme.is_active
  } : emptyWizardForm()
  previewRecords.value = []
  previewTotal.value = 0
  if (scheme?.headers?.length) loadWizardPreview()
}

function resetWizard() {
  wizardStep.value = 0
  wizardScheme.value = null
  selectedFile.value = null
  wizardUploadError.value = ''
  wizardForm.value = emptyWizardForm()
  previewRecords.value = []
  previewTotal.value = 0
  wizardUploadRef.value?.clearFiles()
}

function handleWizardFileChange(file) {
  wizardUploadError.value = ''
  selectedFile.value = file.raw || file
}

function handleWizardFileRemove() {
  selectedFile.value = null
  wizardUploadError.value = ''
}

async function uploadWizardFile() {
  if (!selectedFile.value) {
    ElMessage.warning('请选择要上传的文件')
    return
  }
  const fileName = selectedFile.value.name || ''
  if (!/\.(xls|xlsx|xlsm)$/i.test(fileName)) {
    wizardUploadError.value = '仅支持 .xls / .xlsx / .xlsm 文件'
    ElMessage.warning(wizardUploadError.value)
    return
  }
  uploading.value = true
  wizardUploadError.value = ''
  try {
    if (!wizardScheme.value) {
      const title = selectedFile.value.name.replace(/\.(xlsx|xlsm|xls)$/i, '') || '未命名查分项目'
      wizardScheme.value = await scoreQueryApi.createScheme({ title, is_active: false })
      wizardForm.value.title = title
      wizardForm.value.is_active = true
    }
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    const res = await scoreQueryApi.uploadExcel(wizardScheme.value.id, formData)
    wizardForm.value.headers = res.headers || []
    wizardForm.value.query_conditions = (res.query_conditions || []).filter(cond => wizardForm.value.headers.includes(cond))
    wizardForm.value.display_fields = [...wizardForm.value.headers]
    wizardForm.value.field_groups = autoBuildGroups(wizardForm.value.headers, wizardForm.value.query_conditions, wizardForm.value.display_fields)
    wizardScheme.value.headers = wizardForm.value.headers
    wizardScheme.value.query_conditions = wizardForm.value.query_conditions
    await loadWizardPreview()
    await loadSchemes()
    ElMessage.success('上传成功，已预览前 10 条数据')
  } catch (e) {
    wizardUploadError.value = e.response?.data?.detail || e.message || '上传失败，请检查文件格式或重新登录后再试'
    ElMessage.error(wizardUploadError.value)
  } finally {
    uploading.value = false
  }
}

async function loadWizardPreview() {
  if (!wizardScheme.value) return
  previewLoading.value = true
  try {
    const res = await scoreQueryApi.getRecords(wizardScheme.value.id, 1, 10)
    previewRecords.value = res.items
    previewTotal.value = res.total
  } finally {
    previewLoading.value = false
  }
}

async function saveWizardSettings() {
  if (!wizardScheme.value) {
    ElMessage.warning('请先上传电子表格')
    return
  }
  if (!wizardForm.value.title.trim()) {
    ElMessage.warning('请输入查询标题')
    return
  }
  if (!wizardForm.value.query_conditions.length) {
    ElMessage.warning('请选择查询条件')
    return
  }
  if (wizardForm.value.analysis_enabled && !wizardForm.value.analysis_metrics.length) {
    ElMessage.warning('请选择要显示的分析数据')
    return
  }
  normalizeFieldGroups()
  if (!wizardForm.value.field_groups.length) {
    autoGroupFields()
  }
  submitting.value = true
  try {
    await scoreQueryApi.updateScheme(wizardScheme.value.id, {
      title: wizardForm.value.title.trim(),
      description: wizardForm.value.query_prompt,
      query_conditions: wizardForm.value.query_conditions,
      query_prompt: wizardForm.value.query_prompt,
      query_image_url: wizardForm.value.query_image_url,
      theme: wizardForm.value.theme,
      display_fields: wizardForm.value.display_fields.length ? wizardForm.value.display_fields : wizardForm.value.headers,
      field_groups: wizardForm.value.field_groups,
      analysis_enabled: wizardForm.value.analysis_enabled,
      analysis_metrics: wizardForm.value.analysis_enabled ? wizardForm.value.analysis_metrics : [],
      is_active: wizardForm.value.is_active
    })
    await loadSchemes()
    wizardStep.value = 2
    ElMessage.success('查分设置已保存')
  } finally {
    submitting.value = false
  }
}

async function toggleActive(scheme) {
  if (!scheme.is_active && !isSchemeReady(scheme)) {
    ElMessage.warning('请先上传成绩表格并设置查询条件')
    return
  }
  try {
    await scoreQueryApi.updateScheme(scheme.id, { is_active: !scheme.is_active })
    scheme.is_active = !scheme.is_active
    ElMessage.success(scheme.is_active ? '已开启查询' : '已暂停查询')
  } catch (e) {}
}

async function confirmDelete(scheme) {
  try {
    await ElMessageBox.confirm(`确定要删除查分项目「${scheme.title}」吗？此操作将同时删除所有成绩数据。`, '删除确认', {
      type: 'warning',
      confirmButtonText: '确认删除',
      cancelButtonText: '取消',
      confirmButtonClass: 'el-button--danger'
    })
    await scoreQueryApi.deleteScheme(scheme.id)
    ElMessage.success('已删除')
    loadSchemes()
  } catch (e) {}
}

async function openPreviewDialog(scheme) {
  previewScheme.value = scheme
  previewPage.value = 1
  previewDialogVisible.value = true
  await loadPreviewPage(1)
}

async function loadPreviewPage(page) {
  previewPage.value = page
  previewLoading.value = true
  try {
    const res = await scoreQueryApi.getRecords(previewScheme.value.id, page, previewLimit)
    previewRecords.value = res.items
    previewTotal.value = res.total
  } catch (e) {
    ElMessage.error('加载预览数据失败')
  } finally {
    previewLoading.value = false
  }
}

async function openLogDialog(scheme) {
  logScheme.value = scheme
  logPage.value = 1
  logRecords.value = []
  logTotal.value = 0
  logDialogVisible.value = true
  await loadLogPage(1)
}

async function loadLogPage(page) {
  if (!logScheme.value) return
  logPage.value = page
  logLoading.value = true
  try {
    const res = await scoreQueryApi.getQueryLogs(logScheme.value.id, page, logLimit)
    logRecords.value = res.items || []
    logTotal.value = res.total || 0
  } catch (e) {
    ElMessage.error('加载查询记录失败')
  } finally {
    logLoading.value = false
  }
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('zh-CN', { hour12: false })
}

function summarizeQueryData(data = {}) {
  const parts = Object.entries(data).map(([key, value]) => `${key}：${value || '-'}`)
  return parts.join('，') || '-'
}

function summarizeMatchedRecord(record = {}) {
  const entries = Object.entries(record).filter(([, value]) => value !== undefined && value !== null && String(value) !== '')
  return entries.slice(0, 8).map(([key, value]) => `${key}：${value}`).join('，') || '-'
}

function simplifyUserAgent(userAgent) {
  if (!userAgent) return '-'
  if (/MicroMessenger/i.test(userAgent)) return '微信浏览器'
  if (/Mobile|Android|iPhone|iPad/i.test(userAgent)) return '移动端浏览器'
  if (/Chrome/i.test(userAgent)) return 'Chrome 浏览器'
  if (/Safari/i.test(userAgent)) return 'Safari 浏览器'
  if (/Firefox/i.test(userAgent)) return 'Firefox 浏览器'
  return userAgent.slice(0, 80)
}
</script>

<style scoped>
.score-query-page {
  padding: 28px;
  max-width: 1320px;
  margin: 0 auto;
  min-height: calc(100vh - 56px);
  background:
    linear-gradient(180deg, rgba(8, 145, 178, 0.05), rgba(255, 255, 255, 0) 220px);
  font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
}
.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 6px;
  color: #152033;
  font-size: 26px;
  font-weight: 800;
}
.title-icon { color: #0891b2; }
.page-subtitle { margin: 0; color: #64748b; font-size: 14px; }
.create-btn {
  height: 42px;
  font-weight: 700;
  border-radius: 10px;
  background: #0891b2;
  border-color: #0891b2;
  box-shadow: 0 10px 22px rgba(8, 145, 178, 0.18);
}
.empty-state {
  padding: 64px 24px;
  text-align: center;
  color: #94a3b8;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #fff;
}
.empty-title { margin: 16px 0 6px; color: #475569; font-size: 18px; font-weight: 700; }
.empty-desc { margin: 0 0 22px; font-size: 14px; }
.schemes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 18px;
}
.scheme-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}
.card-header { padding: 18px 18px 12px; border-bottom: 1px solid #f1f5f9; background: linear-gradient(180deg, #ffffff, #fbfdff); }
.card-title-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.scheme-title { min-width: 0; color: #1e293b; font-size: 16px; font-weight: 750; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.scheme-desc { margin: 8px 0 0; color: #64748b; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.card-meta { display: grid; gap: 7px; padding: 13px 18px; color: #475569; font-size: 13px; }
.card-meta span { display: flex; align-items: center; gap: 6px; }
.card-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 2px; padding: 10px 12px; border-top: 1px solid #f1f5f9; }
.action-btn { height: 28px; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 600; }
.copy-btn { color: #0e7490; }
.share-btn { color: #059669; }
.edit-btn { color: #475569; }
.preview-btn { color: #d97706; }
.logs-btn { color: #7c3aed; }
.toggle-btn { color: #0ea5e9; }
.delete-btn { color: #ef4444; }
.card-link-bar { padding: 9px 16px; background: #f8fafc; color: #94a3b8; border-top: 1px solid #e2e8f0; font-size: 11px; word-break: break-all; }
.wizard-steps { margin-bottom: 22px; }
.wizard-guide {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  margin-bottom: 18px;
  padding: 16px 18px;
  border: 1px solid #ccfbf1;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 70%);
}
.guide-copy span {
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
}
.guide-copy strong {
  display: block;
  margin-top: 4px;
  color: #0f172a;
  font-size: 18px;
}
.guide-copy p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}
.guide-steps {
  display: flex;
  gap: 8px;
}
.guide-steps div {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #dbeafe;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}
.guide-steps b {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #0891b2;
  color: #fff;
  font-size: 12px;
}
.wizard-panel { min-height: 430px; }
.upload-layout {
  display: grid;
  grid-template-columns: minmax(320px, 0.8fr) minmax(0, 1.2fr);
  gap: 20px;
}
.upload-card {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}
.upload-area { width: 100%; }
.upload-area-icon { color: #94a3b8; font-size: 48px; margin-bottom: 8px; }
.step-actions { margin-top: 14px; display: flex; justify-content: flex-end; }
.step-actions .el-button {
  min-height: 40px;
  border-radius: 9px;
}
.preview-box, .live-preview {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);
}
.box-title { margin-bottom: 10px; color: #1e293b; font-size: 15px; font-weight: 800; }
.muted-text, .field-hint { margin: 8px 0 0; color: #94a3b8; font-size: 12px; line-height: 1.5; }
.selected-file {
  margin-top: 10px;
  color: #475569;
  font-size: 13px;
}
.upload-error {
  margin-top: 10px;
  padding: 9px 11px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.preview-meta { margin-bottom: 10px; color: #64748b; font-size: 13px; }
.preview-meta-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.preview-meta-bar span {
  padding: 5px 9px;
  border-radius: 999px;
  background: #f1f5f9;
}
.mobile-record-list {
  display: none;
}
.mobile-record-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.mobile-record-title {
  padding: 9px 11px;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 750;
}
.mobile-record-row {
  display: grid;
  grid-template-columns: minmax(86px, 0.75fr) minmax(0, 1fr);
  gap: 10px;
  padding: 10px 11px;
  border-top: 1px solid #edf2f7;
}
.mobile-record-row span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}
.mobile-record-row strong {
  min-width: 0;
  color: #0f172a;
  font-size: 13px;
  text-align: right;
  word-break: break-word;
}
.settings-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 20px;
}
.settings-form {
  padding: 18px 18px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);
}
.analysis-row { display: grid; gap: 10px; }
.theme-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.image-config {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  width: 100%;
}
.image-preview {
  display: block;
  width: 100%;
  max-height: 130px;
  object-fit: cover;
  border-radius: 10px;
  margin-top: 10px;
  border: 1px solid #e2e8f0;
}
.group-editor {
  display: grid;
  gap: 8px;
  width: 100%;
}
.group-toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.group-edit-card {
  padding: 9px 10px;
  border-radius: 9px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}
.group-title-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.group-empty {
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
}
.phone-shell {
  width: 300px;
  margin: 0 auto;
  padding: 12px;
  border-radius: 26px;
  background: #0f172a;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.22);
}
.mock-page {
  min-height: 520px;
  padding: 18px;
  border-radius: 18px;
  background: #f8fafc;
  overflow: hidden;
}
.mock-theme-blue {
  background: linear-gradient(180deg, #eff6ff 0%, #ffffff 48%, #f8fafc 100%);
  border: 1px solid #bfdbfe;
}
.mock-theme-blue button { background: #2563eb; }
.mock-theme-blue h2 { color: #1d4ed8; }
.mock-theme-blue .mock-input { border-color: #bfdbfe; background: #ffffff; }
.mock-theme-green {
  background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 48%, #f7fee7 100%);
  border: 1px solid #bbf7d0;
}
.mock-theme-green button { background: #16a34a; }
.mock-theme-green h2 { color: #14532d; }
.mock-theme-green .mock-input { border-color: #bbf7d0; background: #ffffff; }
.mock-theme-gold {
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 48%, #fef3c7 100%);
  border: 1px solid #fcd34d;
}
.mock-theme-gold button { background: #d97706; }
.mock-theme-gold h2 { color: #92400e; }
.mock-theme-gold .mock-input { border-color: #fde68a; background: #ffffff; }
.mock-theme-dark { background: #111827; color: #e5e7eb; border: 1px solid #475569; }
.mock-theme-dark h2 { color: #fff; }
.mock-theme-dark p { color: #94a3b8; }
.mock-theme-dark .mock-input { background: #1f2937; border-color: #374151; color: #9ca3af; }
.mock-theme-dark .mock-result-title { color: #e5e7eb; }
.mock-theme-dark .mock-row { border-color: #374151; color: #cbd5e1; }
.mock-theme-dark .mock-row strong { color: #fff; }
.mock-image {
  width: 100%;
  height: 110px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 12px;
}
.mock-page h2 { margin: 0 0 6px; color: #0f172a; font-size: 19px; line-height: 1.25; }
.mock-page p { margin: 0 0 14px; color: #64748b; font-size: 12px; line-height: 1.5; }
.mock-input {
  height: 38px;
  margin-bottom: 8px;
  padding: 0 12px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #94a3b8;
  font-size: 13px;
  display: flex;
  align-items: center;
}
.mock-page button {
  width: 100%;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: #0891b2;
  color: white;
  font-weight: 700;
}
.mock-result-title { margin: 18px 0 8px; color: #334155; font-size: 13px; font-weight: 750; }
.mock-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
}
.mock-row strong { color: #0f172a; text-align: right; }
.mock-analysis { margin-top: 10px; padding: 8px; border-radius: 8px; background: #ecfdf5; color: #047857; font-size: 12px; }
.finish-panel { display: flex; align-items: center; justify-content: center; }
.publish-share-card {
  width: min(760px, 100%);
}
.publish-share-card :deep(.el-result) {
  padding-bottom: 14px;
}
.share-content {
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  gap: 20px;
  align-items: center;
  padding: 18px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #f8fbff;
}
.qr-card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 178px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.qr-card img {
  width: 160px;
  height: 160px;
  display: block;
}
.qr-error {
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}
.share-info {
  min-width: 0;
}
.share-label {
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 750;
}
.share-url {
  max-width: 620px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #eff6ff;
  color: #0e7490;
  word-break: break-all;
  font-size: 13px;
}
.share-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.share-dialog-content {
  display: grid;
  gap: 12px;
}
.dialog-qr-card {
  width: 220px;
  min-height: 220px;
  margin: 0 auto;
}
.dialog-qr-card img {
  width: 196px;
  height: 196px;
}
.preview-pagination { display: flex; justify-content: center; margin-top: 16px; }
.log-kv-list {
  display: grid;
  gap: 4px;
  color: #334155;
  font-size: 12px;
}
.matched-summary {
  display: grid;
  gap: 5px;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
}
.log-mobile-card .mobile-record-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
@media (max-width: 900px) {
  .score-query-page { padding: 18px; }
  .page-header, .upload-layout, .settings-grid { grid-template-columns: 1fr; display: grid; }
  .wizard-guide { grid-template-columns: 1fr; }
  .guide-steps { flex-wrap: wrap; }
  .phone-shell { width: 100%; max-width: 320px; }
}
@media (max-width: 640px) {
  .score-query-page {
    padding: 14px;
  }
  .page-header {
    display: grid;
    gap: 12px;
  }
  .page-title {
    font-size: 22px;
  }
  .create-btn {
    width: 100%;
  }
  .schemes-grid {
    grid-template-columns: 1fr;
  }
  .scheme-card {
    border-radius: 10px;
  }
  .card-title-row {
    align-items: flex-start;
  }
  .card-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    padding: 12px;
  }
  .action-btn {
    width: 100%;
    justify-content: center;
    height: 34px;
  }
  .wizard-dialog :deep(.el-dialog) {
    width: calc(100vw - 16px) !important;
    margin: 8px auto !important;
    max-height: calc(100vh - 16px);
    max-height: calc(100dvh - 16px);
    display: flex;
    flex-direction: column;
  }
  .wizard-dialog :deep(.el-dialog__body) {
    overflow: auto;
    padding: 14px;
  }
  .wizard-dialog :deep(.el-dialog__footer) {
    padding: 10px 14px 14px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .wizard-dialog :deep(.dialog-footer),
  .wizard-dialog :deep(.el-dialog__footer .el-button) {
    margin-left: 0;
    width: 100%;
    min-height: 40px;
  }
  .wizard-steps :deep(.el-step__title) {
    font-size: 12px;
    line-height: 1.2;
  }
  .wizard-panel {
    min-height: 0;
  }
  .share-content {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .qr-card {
    min-height: 190px;
  }
  .share-actions {
    display: grid;
    grid-template-columns: 1fr;
  }
  .share-actions .el-button {
    width: 100%;
    margin-left: 0;
  }
  .share-dialog :deep(.el-dialog) {
    width: calc(100vw - 16px) !important;
    margin: 8px auto !important;
  }
  .share-dialog :deep(.el-dialog__body) {
    padding: 14px;
  }
  .share-dialog :deep(.el-dialog__footer) {
    display: grid;
    gap: 8px;
    padding: 10px 14px 14px;
  }
  .share-dialog :deep(.el-dialog__footer .el-button) {
    width: 100%;
    margin-left: 0;
  }
  .guide-steps {
    display: grid;
    grid-template-columns: 1fr;
  }
  .guide-steps div {
    border-radius: 10px;
  }
  .desktop-preview-table {
    display: none;
  }
  .mobile-record-list {
    display: grid;
    gap: 10px;
    max-height: 360px;
    overflow: auto;
  }
  .dialog-mobile-list {
    max-height: calc(100vh - 260px);
    max-height: calc(100dvh - 260px);
  }
  .preview-dialog :deep(.el-dialog) {
    width: calc(100vw - 16px) !important;
    margin: 8px auto !important;
  }
  .preview-dialog :deep(.el-dialog__body) {
    padding: 14px;
  }
  .preview-pagination {
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 4px;
  }
  .mobile-record-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .mobile-record-row strong {
    text-align: left;
    font-size: 14px;
  }
}
</style>
